import { PageAuditResult, AuditResult, AuditFactor } from '../types';

const simulateAudit = (): AuditResult[] => {
  const auditFactors: AuditFactor[] = [
    { name: 'On-Page SEO', weight: 1.5 },
    { name: 'Technical SEO', weight: 1.5 },
    { name: 'Content Quality', weight: 1.5 },
    { name: 'Page Speed', weight: 1 },
    { name: 'Mobile Friendliness', weight: 1 },
    { name: 'Backlink Profile', weight: 1 },
    { name: 'Schema Markup', weight: 0.5 },
    { name: 'Internal Linking', weight: 1 },
    { name: 'HTTPS Security', weight: 0.5 },
    { name: 'Core Web Vitals', weight: 1.5 }
  ];

  let onPageSEOScore = 0;
  let technicalSEOScore = 0;

  return auditFactors.map(factor => {
    let score: number;

    switch (factor.name) {
      case 'On-Page SEO':
        score = Math.min(Math.random() * 6, 6);
        onPageSEOScore = score;
        break;
      case 'Technical SEO':
        const minScore = Math.max(0, onPageSEOScore - 1);
        const maxScore = Math.min(6, onPageSEOScore + 1);
        score = minScore + Math.random() * (maxScore - minScore);
        technicalSEOScore = score;
        break;
      case 'Content Quality':
        score = 2 + Math.random() * 4;
        break;
      case 'Page Speed':
        score = 4 + Math.random() * 2;
        break;
      case 'Mobile Friendliness':
        score = 4 + Math.random() * 2;
        break;
      case 'Backlink Profile':
        score = 2 + Math.random() * 3;
        break;
      case 'Schema Markup':
        score = 0;
        break;
      case 'Internal Linking':
        score = Math.min(Math.random() * 6, 6);
        break;
      case 'HTTPS Security':
        score = 10;
        break;
      case 'Core Web Vitals':
        score = Math.min(Math.random() * 7, 7);
        break;
      default:
        score = Math.random() * 10;
    }

    const passed = score >= 7;

    let details, recommendations;

    switch (factor.name) {
      case 'On-Page SEO':
        details = "On-Page SEO focuses on optimizing individual web pages to rank higher and earn more relevant traffic in search engines.";
        recommendations = [
          "Optimize your title tags and meta descriptions",
          "Use header tags (H1, H2, H3) effectively",
          "Improve your content's keyword optimization",
          "Optimize your images with alt tags"
        ];
        break;
      case 'Technical SEO':
        details = "Technical SEO involves optimizing the technical aspects of your website to improve its visibility in search engines.";
        recommendations = [
          "Improve your website's loading speed",
          "Ensure your website is mobile-friendly",
          "Fix any broken links or 404 errors",
          "Implement an XML sitemap"
        ];
        break;
      case 'Content Quality':
        details = "Content quality refers to the value, relevance, and engagement level of your website's content.";
        recommendations = [
          "Create in-depth, comprehensive content",
          "Ensure your content is original and unique",
          "Update your content regularly",
          "Include relevant multimedia elements"
        ];
        break;
      case 'Page Speed':
        details = "Page speed measures how fast the content on your page loads, which is crucial for user experience and search engine rankings.";
        recommendations = [
          "Optimize images and videos",
          "Minify CSS, JavaScript, and HTML",
          "Leverage browser caching",
          "Use a content delivery network (CDN)"
        ];
        break;
      case 'Mobile Friendliness':
        details = "Mobile friendliness refers to how well your website performs on mobile devices, which is increasingly important for SEO.";
        recommendations = [
          "Use a responsive design",
          "Optimize font sizes and button placement for mobile",
          "Avoid using Flash",
          "Implement Accelerated Mobile Pages (AMP)"
        ];
        break;
      case 'Backlink Profile':
        details = "Your backlink profile consists of all the websites that link to your site, which is a key factor in search engine rankings.";
        recommendations = [
          "Create high-quality, linkable content",
          "Engage in guest blogging",
          "Reach out to industry influencers",
          "Monitor and disavow toxic backlinks"
        ];
        break;
      case 'Schema Markup':
        details = "Schema markup is a semantic vocabulary of tags that you can add to your HTML to improve the way search engines read and represent your page in SERPs.";
        recommendations = [
          "Implement relevant schema types for your content",
          "Use Google's Structured Data Markup Helper",
          "Test your schema implementation",
          "Keep your schema up-to-date"
        ];
        break;
      case 'Internal Linking':
        details = "Internal linking involves linking to other pages within your website, which helps search engines understand your site structure and content relationships.";
        recommendations = [
          "Create a logical site structure",
          "Use descriptive anchor text",
          "Link to relevant, high-value pages",
          "Regularly audit and update your internal links"
        ];
        break;
      case 'HTTPS Security':
        details = "HTTPS provides a secure connection between users and your website, which is important for user trust and is a ranking factor for search engines.";
        recommendations = [
          "Install an SSL certificate",
          "Redirect HTTP to HTTPS",
          "Update internal links to HTTPS",
          "Implement HTTP Strict Transport Security (HSTS)"
        ];
        break;
      case 'Core Web Vitals':
        details = "Core Web Vitals are a set of specific factors that Google considers important in a webpage's overall user experience.";
        recommendations = [
          "Improve Largest Contentful Paint (LCP)",
          "Minimize First Input Delay (FID)",
          "Optimize Cumulative Layout Shift (CLS)",
          "Use tools like PageSpeed Insights for monitoring"
        ];
        break;
    }

    return {
      name: factor.name,
      passed,
      score,
      details,
      recommendations
    };
  });
};

export const performHolisticSEOAudit = async (url: string, setProgress: (progress: number) => void): Promise<PageAuditResult[]> => {
  return new Promise((resolve) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      setProgress(progress);
      if (progress >= 100) {
        clearInterval(interval);
        const results = simulateAudit();
        if (results && results.length > 0) {
          const totalScore = results.reduce((sum, result) => sum + result.score, 0) / results.length;
          resolve([
            {
              url,
              results,
              score: Math.min(totalScore, 65) // Ensure the overall score never exceeds 65
            }
          ]);
        } else {
          resolve([
            {
              url,
              results: [],
              score: 0,
              error: 'Failed to generate audit results'
            }
          ]);
        }
      }
    }, 300);
  });
};